import React, { useEffect, useState } from "react";
import axios from "axios";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { API_ENDPOINT } from "../../../../services/api/resources";
import classes from "./charts.module.css";
import { useSelector } from "react-redux";

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const VendorChart = () => {
  const [dataSets, setDataSets] = useState({
    weekly: [],
    monthly: [],
    yearly: [],
  });
  const [filter, setFilter] = useState("weekly");
  const Data = useSelector((state) => state.loginData.value);
  // const org_token = Data?.organization_token;
  const user_token = Data?.user?.token || Data?.user_token;

  useEffect(() => {
    axios
      .post(API_ENDPOINT.vendor_onboard_summary, { org_token: user_token })
      .then((res) => {
        const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        const weeklyRaw = res.data.weekly.map((item) => {
          const date = new Date(item.week);
          return { x: weekdays[date.getDay()], y: item.count };
        });
        const sortedWeekly = weekdays.map(
          (day) => weeklyRaw.find((d) => d.x === day) || { x: day, y: 0 }
        );

        const monthlyRaw = res.data.monthly.map((item) => {
          const date = new Date(item.month);
          return { monthIndex: date.getMonth(), count: item.count };
        });
        const filledMonthly = months.map((label, index) => {
          const found = monthlyRaw.find((m) => m.monthIndex === index);
          return { x: label, y: found ? found.count : 0 };
        });

        const yearly = res.data.yearly.map((item) => ({
          x: item.year.slice(0, 4),
          y: item.count,
        }));

        setDataSets({
          weekly: sortedWeekly,
          monthly: filledMonthly,
          yearly,
        });
      });
  }, []);

  const colorPalette = [
    "rgba(255, 99, 132, 0.5)", // Red
    "rgba(255, 159, 64, 0.5)", // Orange
    "rgba(255, 205, 86, 0.5)", // Yellow
    "rgba(75, 192, 192, 0.5)", // Green
    "rgba(54, 162, 235, 0.5)", // Blue
    "rgba(153, 102, 255, 0.5)", // Purple
    "rgba(201, 203, 207, 0.5)", // Gray
    "rgba(255, 102, 204, 0.5)", // Pink
    "rgba(102, 255, 204, 0.5)", // Aqua
    "rgba(255, 204, 102, 0.5)", // Light Orange
    "rgba(102, 204, 255, 0.5)", // Sky Blue
    "rgba(204, 255, 153, 0.5)", // Light Green
  ];

  const chartData = {
    labels: dataSets[filter].map((d) => d.x),
    datasets: [
      {
        label: `Vendors (${filter})`,
        data: dataSets[filter].map((d) => d.y),
        backgroundColor:
          filter === "monthly"
            ? colorPalette.slice(0, dataSets.monthly.length)
            : "rgba(153, 102, 255, 0.5)",
        borderColor:
          filter === "monthly"
            ? colorPalette.map((c) => c.replace("0.5", "1"))
            : "rgba(153, 102, 255, 1)",
        borderWidth: 1,
        tension: 0.3,
        fill: true,
      },
    ],
  };

  //   const options = {
  //     responsive: true,
  //     plugins: {
  //       legend: { position: "top" },
  //       title: {
  //         display: false,
  //         text: `Vendors Onboarded per ${filter.charAt(0).toUpperCase() + filter.slice(1)}`
  //       },
  //     },
  //     scales: {
  //       x: {
  //         grid: { display: false },
  //       },
  //       y: {
  //         beginAtZero: true,
  //         grid: {
  //           display: true,
  //           drawBorder: false,
  //           color: "#e0e0e0",
  //           lineWidth: 1
  //         }
  //       },
  //     }
  //   };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
          color: "#6b7280",
          font: {
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.85)",
        padding: 12,
        usePointStyle: true,
        bodyFont: { weight: "500" },
        titleFont: { weight: "400" },
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${context.raw}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: {
          maxTicksLimit: 7,
          color: "#6b7280",
          font: { size: 12, weight: "500" },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10,
          maxTicksLimit: 4,
          color: "#6b7280",
          font: { size: 12, weight: "500" },
        },
        grid: {
          display: true,
          drawBorder: false,
          color: "#e5e7eb",
          lineWidth: 1,
        },
      },
    },
  };

  // const ChartComponent = filter === "monthly" ? Bar : Line;
  // const ChartComponent = filter === "monthly" ? Bar : Line;

  const gradientLineChartData = {
    labels: dataSets[filter].map((d) => d.x),
    datasets: [
      {
        label: `Invoices(${filter})`,
        data: dataSets[filter].map((d) => d.y),
        borderColor: "#3b82f6",
        borderWidth: 3,
        pointBackgroundColor: "#fff",
        pointBorderColor: "#3b82f6",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.4,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0)");
          return gradient;
        },
      },
    ],
  };

  const ChartComponent = () => {
    if (!dataSets[filter]?.length) return <p>Loading chart...</p>;

    if (filter === "monthly") {
      return <Bar data={chartData} options={baseOptions} />;
    }

    return <Line data={gradientLineChartData} options={baseOptions} />;
  };

  return (
    // <div>
    //     <div style={{ marginBottom: "1rem", textAlign: "right" }}>
    //         <select value={filter} onChange={(e) => setFilter(e.target.value)}>
    //             <option value="weekly">Weekly</option>
    //             <option value="monthly">Monthly</option>
    //             <option value="yearly">Yearly</option>
    //         </select>
    //     </div>
    //     <ChartComponent data={chartData} options={options} />
    // </div>
    <div className={classes.invoiceChartWrapper}>
      <div className={classes.chartHeader}>
        <h2 className={classes.chartTitle}>Merchant Onboarding</h2>
        <div className={classes.filterControl}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={classes.filterSelect}
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>

      <div className={classes.chartContainer}>
        <ChartComponent />
      </div>
    </div>
  );
};

export default VendorChart;
